import { extractText } from "../chat/message-extract.ts";
import type { GatewayBrowserClient } from "../gateway.ts";
import type { ChatAttachment } from "../ui-types.ts";
import { generateUUID } from "../uuid.ts";

export type ChatState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  sessionKey: string;
  chatLoading: boolean;
  chatMessages: unknown[];
  chatThinkingLevel: string | null;
  chatSending: boolean;
  chatMessage: string;
  chatAttachments: ChatAttachment[];
  chatRunId: string | null;
  chatStream: string | null;
  stream?: { type: "thinking" | "text" } | null;
  chatStreamStartedAt: number | null;
  lastError: string | null;
};

type ChatHistoryResponse = {
  messages?: unknown[];
  thinkingLevel?: string;
};

export type ChatEventPayload = {
  runId: string;
  sessionKey: string;
  state: "delta" | "final" | "aborted" | "error";
  message?: unknown;
  errorMessage?: string;
};

type LoadChatHistoryOptions = {
  allowEmpty?: boolean;
  maxAttempts?: number;
  retryDelayMs?: number;
};

const FALLBACK_FINAL_MESSAGE = "Task completed. See Reasoning steps for the execution details.";

type OperationalTranscriptSummary = {
  isOperational: boolean;
  summary: string | null;
};

function formatOperationalActionList(actions: string[]): string {
  const limit = 4;
  if (actions.length <= limit) {
    return actions.join(", ");
  }
  return `${actions.slice(0, limit).join(", ")}, ...`;
}

function summarizeOperationalTranscript(text: string): OperationalTranscriptSummary {
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (lines.length < 3) {
    return { isOperational: false, summary: null };
  }

  let recognized = 0;
  let completedTasks = 0;
  let browserActions = 0;
  let hasFailure = false;
  const browserActionKinds: string[] = [];
  let hasInteractiveBrowserAction = false;

  for (const line of lines) {
    const taskMatch = line.match(/^Task\s+[^:]+:\s+(.+?)\s*->\s*(running|completed|failed)\s*$/i);
    if (taskMatch) {
      recognized += 1;
      const status = taskMatch[2]?.toLowerCase();
      if (status === "completed") {
        completedTasks += 1;
      }
      if (status === "failed") {
        hasFailure = true;
      }
      continue;
    }
    const browserMatch = line.match(/^Browser action\s+([a-z0-9_.-]+)\s+(success|failed)\b/i);
    if (browserMatch) {
      recognized += 1;
      browserActions += 1;
      const actionName = (browserMatch[1] ?? "").toLowerCase();
      if (actionName && !browserActionKinds.includes(actionName)) {
        browserActionKinds.push(actionName);
      }
      if (
        actionName &&
        actionName !== "navigate" &&
        actionName !== "screenshot" &&
        actionName !== "open_live_view"
      ) {
        hasInteractiveBrowserAction = true;
      }
      if (browserMatch[2]?.toLowerCase() !== "success") {
        hasFailure = true;
      }
      continue;
    }
    if (/^Mission\s+[^\s]+\s+updated:\s*([a-z_]+)\s*$/i.test(line)) {
      recognized += 1;
      if (/updated:\s*failed\s*$/i.test(line)) {
        hasFailure = true;
      }
      continue;
    }
    if (/^Mission accepted:\s+[^\s]+\s+\(([^)]+)\)\s*$/i.test(line)) {
      recognized += 1;
      if (/\(failed\)\s*$/i.test(line)) {
        hasFailure = true;
      }
      continue;
    }
    if (
      /^Routed message to mission\b/i.test(line) ||
      /^executor\.[a-z0-9_.-]+\b/i.test(line) ||
      /^Incident\b/i.test(line)
    ) {
      recognized += 1;
      if (/^Incident\b/i.test(line)) {
        hasFailure = true;
      }
    }
  }

  const isOperational = recognized >= 2 && recognized / Math.max(1, lines.length) >= 0.55;
  if (!isOperational) {
    return { isOperational: false, summary: null };
  }

  const actionsLabel =
    browserActionKinds.length > 0 ? formatOperationalActionList(browserActionKinds) : null;

  if (hasFailure) {
    const parts: string[] = [];
    if (completedTasks > 0) {
      parts.push(`after ${completedTasks} completed step${completedTasks === 1 ? "" : "s"}`);
    }
    const prefix =
      parts.length > 0
        ? `Run finished with issues ${parts.join(" ")}.`
        : "Run finished with issues.";
    return {
      isOperational: true,
      summary: `${prefix}${actionsLabel ? ` Browser actions: ${actionsLabel}.` : ""} Check Reasoning steps for details.`,
    };
  }

  if (browserActions > 0 && !hasInteractiveBrowserAction) {
    return {
      isOperational: true,
      summary: `Run completed with basic browser steps${actionsLabel ? ` (${actionsLabel})` : ""}. No interactive actions were executed yet.`,
    };
  }

  const parts: string[] = [];
  if (completedTasks > 0) {
    parts.push(`${completedTasks} step${completedTasks === 1 ? "" : "s"}`);
  }
  if (browserActions > 0) {
    parts.push(`${browserActions} browser action${browserActions === 1 ? "" : "s"}`);
  }
  if (parts.length === 0) {
    return {
      isOperational: true,
      summary: "Run completed. Check Reasoning steps for details.",
    };
  }

  return {
    isOperational: true,
    summary: `Done. Completed ${parts.join(" and ")}${actionsLabel ? ` (${actionsLabel})` : ""}.`,
  };
}

function sleep(ms: number): Promise<void> {
  if (ms <= 0) {
    return Promise.resolve();
  }
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function getMaxMessageTimestamp(messages: unknown[]): number | null {
  let max: number | null = null;
  for (const msg of messages) {
    if (!msg || typeof msg !== "object" || Array.isArray(msg)) {
      continue;
    }
    const ts = (msg as { timestamp?: unknown }).timestamp;
    if (typeof ts !== "number" || !Number.isFinite(ts)) {
      continue;
    }
    if (max === null || ts > max) {
      max = ts;
    }
  }
  return max;
}

export async function loadChatHistory(state: ChatState, opts: LoadChatHistoryOptions = {}) {
  if (!state.client || !state.connected) {
    return;
  }
  const sessionKey = state.sessionKey;
  const client = state.client;
  const allowEmpty = opts.allowEmpty ?? true;
  const maxAttempts = Math.max(1, opts.maxAttempts ?? 1);
  const baseDelayMs = Math.max(0, opts.retryDelayMs ?? 0);

  state.chatLoading = true;
  state.lastError = null;
  try {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      const res = await client.request<ChatHistoryResponse>("chat.history", {
        sessionKey,
        limit: 80,
      });
      if (state.sessionKey !== sessionKey) {
        return;
      }

      const messages = Array.isArray(res.messages) ? res.messages : [];
      const currentMaxTimestamp = getMaxMessageTimestamp(state.chatMessages);
      const incomingMaxTimestamp = getMaxMessageTimestamp(messages);
      const incomingLooksStale =
        !allowEmpty &&
        state.chatMessages.length > 0 &&
        messages.length > 0 &&
        currentMaxTimestamp !== null &&
        incomingMaxTimestamp !== null &&
        incomingMaxTimestamp < currentMaxTimestamp;
      const shouldAcceptEmpty =
        allowEmpty || messages.length > 0 || state.chatMessages.length === 0;

      // Avoid wiping the UI thread right after a final event. The gateway may emit streaming
      // events ahead of session persistence, so a history read can temporarily be empty or stale.
      if (
        !allowEmpty &&
        state.chatMessages.length > 0 &&
        attempt < maxAttempts &&
        (messages.length === 0 || incomingLooksStale)
      ) {
        const delayMs = baseDelayMs ? baseDelayMs * attempt : 200 * attempt;
        await sleep(delayMs);
        continue;
      }

      if (shouldAcceptEmpty && (!incomingLooksStale || state.chatMessages.length === 0)) {
        state.chatMessages = messages;
      }
      state.chatThinkingLevel = res.thinkingLevel ?? null;
      break;
    }
  } catch (err) {
    state.lastError = String(err);
  } finally {
    state.chatLoading = false;
  }
}

function dataUrlToBase64(dataUrl: string): { content: string; mimeType: string } | null {
  const match = /^data:([^;]+);base64,(.+)$/.exec(dataUrl);
  if (!match) {
    return null;
  }
  return { mimeType: match[1], content: match[2] };
}

function normalizeAbortedAssistantMessage(message: unknown): Record<string, unknown> | null {
  if (!message || typeof message !== "object") {
    return null;
  }
  const candidate = message as Record<string, unknown>;
  if (candidate.role !== "assistant") {
    return null;
  }
  if (!("content" in candidate) || !Array.isArray(candidate.content)) {
    return null;
  }
  return candidate;
}

export async function sendChatMessage(
  state: ChatState,
  message: string,
  attachments?: ChatAttachment[],
): Promise<string | null> {
  if (!state.client || !state.connected) {
    return null;
  }
  const msg = message.trim();
  const hasAttachments = attachments && attachments.length > 0;
  if (!msg && !hasAttachments) {
    return null;
  }

  const now = Date.now();

  // Build user message content blocks
  const contentBlocks: Array<{ type: string; text?: string; source?: unknown }> = [];
  if (msg) {
    contentBlocks.push({ type: "text", text: msg });
  }
  // Add image previews to the message for display
  if (hasAttachments) {
    for (const att of attachments) {
      contentBlocks.push({
        type: "image",
        source: { type: "base64", media_type: att.mimeType, data: att.dataUrl },
      });
    }
  }

  state.chatMessages = [
    ...state.chatMessages,
    {
      role: "user",
      content: contentBlocks,
      timestamp: now,
    },
  ];

  state.chatSending = true;
  state.lastError = null;
  const runId = generateUUID();
  state.chatRunId = runId;
  state.chatStream = "";
  state.stream = { type: "thinking" };
  state.chatStreamStartedAt = now;

  // Convert attachments to API format
  const apiAttachments = hasAttachments
    ? attachments
        .map((att) => {
          const parsed = dataUrlToBase64(att.dataUrl);
          if (!parsed) {
            return null;
          }
          return {
            type: "image",
            mimeType: parsed.mimeType,
            content: parsed.content,
          };
        })
        .filter((a): a is NonNullable<typeof a> => a !== null)
    : undefined;

  try {
    console.log("[TDAI Chat] Sending message:", {
      sessionKey: state.sessionKey,
      runId,
      messageLength: msg.length,
    });
    await state.client.request("chat.send", {
      sessionKey: state.sessionKey,
      message: msg,
      deliver: false,
      idempotencyKey: runId,
      attachments: apiAttachments,
    });
    console.log("[TDAI Chat] Message sent successfully, runId:", runId);
    return runId;
  } catch (err) {
    const error = String(err);
    console.error("[TDAI Chat] Send error:", error);
    state.chatRunId = null;
    state.chatStream = null;
    state.stream = null;
    state.chatStreamStartedAt = null;
    state.lastError = error;
    state.chatMessages = [
      ...state.chatMessages,
      {
        role: "assistant",
        content: [{ type: "text", text: "Error: " + error }],
        timestamp: Date.now(),
      },
    ];
    return null;
  } finally {
    state.chatSending = false;
  }
}

export async function abortChatRun(state: ChatState): Promise<boolean> {
  if (!state.client || !state.connected) {
    return false;
  }
  const runId = state.chatRunId;
  try {
    await state.client.request(
      "chat.abort",
      runId ? { sessionKey: state.sessionKey, runId } : { sessionKey: state.sessionKey },
    );
    return true;
  } catch (err) {
    state.lastError = String(err);
    return false;
  }
}

export function handleChatEvent(state: ChatState, payload?: ChatEventPayload) {
  if (!payload) {
    return null;
  }
  if (payload.sessionKey !== state.sessionKey) {
    return null;
  }

  // Final from another run (e.g. sub-agent announce): refresh history to show new message.
  // See https://github.com/openclaw/openclaw/issues/1909
  if (payload.runId && state.chatRunId && payload.runId !== state.chatRunId) {
    if (payload.state === "final") {
      return "final";
    }
    return null;
  }

  if (payload.state === "delta") {
    const next = payload.message ? extractText(payload.message) : null;
    if (typeof next === "string") {
      // Operational transcripts belong in the Reasoning panel; keep the main chat stream clean.
      const operational = summarizeOperationalTranscript(next);
      if (operational.isOperational) {
        state.chatStream = "";
        state.stream = { type: "thinking" };
        return payload.state;
      }
      const current = state.chatStream ?? "";
      if (!current || next.length >= current.length) {
        state.chatStream = next;
        state.stream = { type: "text" };
      }
    }
  } else if (payload.state === "final") {
    const hadActiveRun =
      Boolean(state.chatRunId && payload.runId === state.chatRunId) ||
      typeof state.chatStream === "string" ||
      state.chatStreamStartedAt !== null;
    const final = payload.message ? extractText(payload.message) : null;
    const finalText =
      typeof final === "string" && final.trim()
        ? final.trim()
        : typeof state.chatStream === "string" && state.chatStream.trim()
          ? state.chatStream.trim()
          : null;
    const operational = finalText ? summarizeOperationalTranscript(finalText) : null;
    const userFacingText = operational?.summary ?? finalText;

    if (userFacingText) {
      state.chatMessages = [
        ...state.chatMessages,
        {
          role: "assistant",
          content: [{ type: "text", text: userFacingText }],
          timestamp: Date.now(),
        },
      ];
    } else if (hadActiveRun) {
      state.chatMessages = [
        ...state.chatMessages,
        {
          role: "assistant",
          content: [{ type: "text", text: FALLBACK_FINAL_MESSAGE }],
          timestamp: Date.now(),
        },
      ];
    }

    state.chatStream = null;
    state.stream = null;
    state.chatRunId = null;
    state.chatStreamStartedAt = null;
  } else if (payload.state === "aborted") {
    // Upstream: attempt to preserve the partial assistant message from the aborted payload.
    const normalizedMessage = normalizeAbortedAssistantMessage(payload.message);
    if (normalizedMessage) {
      state.chatMessages = [...state.chatMessages, normalizedMessage];
    } else {
      const streamedText = state.chatStream ?? "";
      if (streamedText.trim()) {
        state.chatMessages = [
          ...state.chatMessages,
          {
            role: "assistant",
            content: [{ type: "text", text: streamedText }],
            timestamp: Date.now(),
          },
        ];
      }
    }
    state.chatStream = null;
    state.stream = null;
    state.chatRunId = null;
    state.chatStreamStartedAt = null;
  } else if (payload.state === "error") {
    state.chatStream = null;
    state.stream = null;
    state.chatRunId = null;
    state.chatStreamStartedAt = null;
    state.lastError = payload.errorMessage ?? "chat error";
  }
  return payload.state;
}
